import { Badge, type DataTableColumn } from "@vjal/ui";
import { humanizeStatus } from "../lib/status-format";
import { CohortDetailPage } from "./cohorts/cohort-detail-page";
import type { ProductScreenContract, TableRow } from "./product-route-contracts";

// ── Cohort workspace (Wave 3 — REBUILT as a gold-pattern detail page) ───────
// The thin contract-surface cohort workspace (a static Segmented tab strip over
// fixture rows) was replaced by a real CohortDetailPage with REAL URL-driven tab
// routing: the active tab lives in the URL (`?tab=<id>`, deep-linkable, browser
// back/forward works), and each tab is its OWN component wrapping its OWN
// useHonestQuery -> <HonestState> read. Tabs: Overview / Participants / Sessions /
// Invites / Attendance / Progress / Reports / Recordings / Assignments.
//
// These are READ tabs — the deep WRITE flows are deferred to their owning waves
// (Participants -> Wave 4, create session/series -> Wave 6, generate report ->
// Wave 7); each tab marks its deferral and fabricates no write. The header
// Edit/Complete affordances render only for the write capabilities
// (super_admin/admin/coach). The Reports tab is REPORT-EXCLUDED (status only) for
// every role. The contract factory below preserves the route registration +
// provenance metadata and renders the new page via `customBody`.

const cohortSessionRows: TableRow[] = [
  { id: "session-1", session: "Session 1", date: "2026-03-08 14:00", status: "scheduled", output: "report ready" }
];

export function cohortWorkspaceScreen(): ProductScreenContract {
  return {
    title: () => "Cohort workspace",
    primaryJob:
      "Inspect one cohort across Overview, Participants, Sessions, Invites, Attendance, Progress, Reports, Recordings, and Assignments via URL-driven tabs.",
    secondaryJob:
      "Each tab is a real read; staff/coach also get Edit + Complete from the header. The Reports tab shows status only and is report-excluded for every role.",
    workTitle: "Cohort delivery workspace",
    rows: cohortSessionRows,
    columns: sessionColumns(),
    states: ["loading", "empty", "permission-denied", "unavailable", "gate-closed", "error", "read-only"],
    apiFamilies: ["cohort-service", "participant-service", "session-service", "report-service", "history-service"],
    roleDeltas:
      "Admin/coach get Edit + Complete + every read tab; company_admin/cohort_lead are read-only; participant reaches it only via My Cohorts and the server pins scope. Reports tab is report-excluded for all.",
    extensionSlots: ["add participant (Wave 4)", "create session/series (Wave 6)", "generate report (Wave 7)"],
    mobilePosture: "Tabs, header, and each tab's table stack with peer privacy preserved and no horizontal overflow.",
    evidenceRequirement:
      "Playwright must prove tab routing (URL updates, deep-link, back/forward), each tab's real read, report-excluded Reports tab, and read-only roles.",
    surfaceMode: "focused",
    customBody: CohortDetailPage
  };
}

function sessionColumns(): DataTableColumn<TableRow>[] {
  return [
    { key: "session", header: "Session" },
    { key: "date", header: "Date" },
    { key: "status", header: "Status", render: (row) => <Badge variant="info">{humanizeStatus(String(row.status))}</Badge> },
    { key: "output", header: "Output" }
  ];
}
